// src/features/ownerAdmin/CreatorReportsPage.tsx
import { useState } from 'react';
import { useBugReports, useUpdateReport, type ReportStatus, type BugReport } from './useCreatorData';
import { formatRelative } from '../../lib/formatTime';
import './OwnerAdminPage.css';

const STATUS_OPTIONS: (ReportStatus | 'all')[] = ['unresolved', 'needs_info', 'resolved', 'skipped', 'all'];

function ReportRow({ report }: { report: BugReport }) {
  const [note, setNote] = useState(report.creator_note ?? '');
  const update = useUpdateReport();

  return (
    <article className="card" style={{ marginBottom: '0.75rem' }}>
      <div className="card__content">
        <div className="owner-admin-report__header">
          <span className="status-badge status-badge--warning">{report.category}</span>
          <span className="owner-admin-table__mono" style={{ fontSize: 11 }}>{report.source} · {formatRelative(report.submitted_at)}</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: '0.6rem 0' }}>{report.description}</p>
        {report.screenshot && (
          <a href={report.screenshot} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>
            View screenshot
          </a>
        )}
        <div className="owner-admin-report__actions">
          <select
            className="input"
            value={report.status}
            onChange={(e) => update.mutate({ id: report.id, status: e.target.value as ReportStatus })}
            style={{ maxWidth: 160 }}
          >
            <option value="unresolved">Unresolved</option>
            <option value="needs_info">Needs info</option>
            <option value="resolved">Resolved</option>
            <option value="skipped">Skipped</option>
          </select>
          <input
            className="input"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => { if (note !== (report.creator_note ?? '')) update.mutate({ id: report.id, creator_note: note }); }}
            placeholder="Internal note (saved on blur)"
          />
        </div>
      </div>
    </article>
  );
}

export function CreatorReportsPage() {
  const [status, setStatus] = useState<ReportStatus | 'all'>('unresolved');
  const { data, isLoading, error } = useBugReports(status);

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Bot owner only</p>
        <h1 className="page-header__title">Bug Reports</h1>
      </div>

      <article className="card">
        <div className="card__content">
          <div className="owner-admin-filter">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                className={status === s ? 'button-primary' : 'button-secondary'}
                onClick={() => setStatus(s)}
              >
                {s}
              </button>
            ))}
          </div>

          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load reports.</p>}
          {data?.reports.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No reports with this status.</p>}
          {data?.reports.map((report) => <ReportRow key={report.id} report={report} />)}
        </div>
      </article>
    </>
  );
}
